import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Trash2, ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DayPicker } from 'react-day-picker';
import { fmt, fmtCur } from './helpers';

export function CostingHistoryModal({ part, onClose }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelInput, setLabelInput] = useState('');
  const [datePickerIdx, setDatePickerIdx] = useState(null);

  useEffect(() => {
    if (!part?.part_id) return;
    api.get(`/parts/${part.part_id}/history`)
      .then(res => setHistory(res.data.history || []))
      .catch(() => toast.error('Failed to load history'))
      .finally(() => setLoading(false));
  }, [part?.part_id]);

  if (!part) return null;

  const patchEntry = async (idx, fields) => {
    try {
      await api.patch(`/parts/${part.part_id}/history/${idx}`, fields);
      setHistory(prev => prev.map((h, i) => (i === idx ? { ...h, ...fields } : h)));
      return true;
    } catch {
      toast.error('Failed to update history');
      return false;
    }
  };

  const saveLabel = async (idx) => {
    if (await patchEntry(idx, { review_label: labelInput })) toast.success('Label updated');
    setEditingLabel(null);
  };

  const saveDate = async (idx, date) => {
    const dateStr = date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\//g, '-');
    if (await patchEntry(idx, { review_date: dateStr, review_label: dateStr })) toast.success('Date updated');
    setDatePickerIdx(null);
  };

  const deleteEntry = async (idx) => {
    if (!window.confirm('Delete this history entry?')) return;
    const removed = history[idx];
    try {
      await api.delete(`/parts/${part.part_id}/history/${idx}`);
      setHistory(prev => prev.filter((_, i) => i !== idx));
      toast.success('Entry deleted', {
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await api.post(`/parts/${part.part_id}/history/restore`, { entry: removed });
              setHistory(prev => [...prev, removed]);
              toast.success('Entry restored');
            } catch {
              toast.error('Failed to restore entry');
            }
          },
        },
      });
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const sorted = [...history].sort((a, b) => {
    const parseDate = (d) => {
      if (!d) return 0;
      const parts = d.split(/[-/.]/);
      if (parts.length === 3) {
        const [d1, m1, y1] = parts.map(Number);
        if (y1 > 1000) return new Date(y1, m1 - 1, d1).getTime();
        if (d1 > 1000) return new Date(d1, m1 - 1, y1).getTime();
      }
      const t = new Date(d).getTime();
      return isNaN(t) ? 0 : t;
    };
    return parseDate(b.review_date) - parseDate(a.review_date);
  });

  const normalized = sorted.map(h => ({
    ...h,
    grand_total: h.grand_total || h.selling_price || 0,
  }));

  const withDelta = normalized.map((h, i) => {
    const prev = i < normalized.length - 1 ? normalized[i + 1] : null;
    const totalChange = prev ? h.grand_total - prev.grand_total : 0;
    return { ...h, totalChange };
  });

  const getDisplayLabel = (h, i) => {
    if (h.review_label) return h.review_label;
    if (h.review_date) return h.review_date;
    return i === 0 ? 'Latest Snapshot' : `Snapshot ${sorted.length - i}`;
  };

  // Live current state sourced directly from the part (not a stored snapshot)
  const liveRow = {
    rm_price_per_kg: part.rm_price_per_kg ?? part.rm_price ?? 0,
    gross_rmc: part.gross_rm_cost ?? 0,
    scrap_recovery: part.scrap_recovery ?? 0,
    net_rmc: part.rm_cost_net ?? 0,
    process_cost: part.process_cost ?? 0,
    plating_cost: part.plating_cost ?? 0,
    total_bop_cost: part.total_bop_cost ?? 0,
    tool_maintenance: part.tool_maintenance_cost ?? 0,
    total_cost: part.total_manufacturing_cost ?? 0,
    overhead: part.overhead_fixed ?? 0,
    packing: part.packing_fixed ?? 0,
    grand_total: part.selling_price ?? 0,
  };
  const liveChange = withDelta.length > 0 ? liveRow.grand_total - withDelta[0].grand_total : 0;

  const getOriginalIdx = (sortedEntry) => history.indexOf(sortedEntry);

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-slate-900">
            Quarterly Costing History — <span className="font-mono text-orange-500">{part.part_number}</span>
          </DialogTitle>
          <p className="text-xs text-slate-500 mt-0.5">{part.part_name} | {part.customer_name}</p>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400">Loading history...</div>
        ) : (
          <div className="overflow-x-auto mt-2 relative">
            <table className="w-full text-xs border-collapse" data-testid="costing-history-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 sticky left-0 z-20 bg-slate-50 min-w-[160px] border-r border-slate-200">Period / Date</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[100px]">RM/Kg</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[75px]">Gross RMC</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[70px]">Scrap</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[75px]">Net RMC</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[70px]">Process</th>
                  <th className="text-right px-3 py-2 font-semibold text-purple-600 min-w-[70px]">Plating</th>
                  <th className="text-right px-3 py-2 font-semibold text-indigo-600 min-w-[70px]">BOP</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[60px]">Tool</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[80px]">Total Cost</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[70px]">O/head</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[65px]">Packing</th>
                  <th className="text-right px-3 py-2 font-semibold text-orange-600 min-w-[90px]">G.Total</th>
                  <th className="text-right px-3 py-2 font-semibold text-slate-600 min-w-[70px]">Change</th>
                  <th className="text-center px-2 py-2 font-semibold text-slate-400 min-w-[36px]"></th>
                </tr>
              </thead>
              <tbody>
                {/* Live Current — sourced directly from the part, not a stored snapshot */}
                <tr className="border-b-2 border-orange-200 bg-orange-50" data-testid="history-row-live">
                  <td className="px-3 py-2.5 sticky left-0 z-10 bg-orange-50 min-w-[160px] border-r border-slate-200">
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0"></span>
                      <span className="font-bold text-orange-700">Live Current</span>
                    </div>
                    <p className="text-[10px] text-orange-500/80 mt-0.5 pl-3">Current saved values</p>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap"><span className="font-bold">{fmt(liveRow.rm_price_per_kg)}</span></td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-700">{fmt(liveRow.gross_rmc)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-emerald-600">-{fmt(liveRow.scrap_recovery)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{fmt(liveRow.net_rmc)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmt(liveRow.process_cost)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-purple-600">{fmt(liveRow.plating_cost)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-indigo-600">{fmt(liveRow.total_bop_cost)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmt(liveRow.tool_maintenance)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{fmt(liveRow.total_cost)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmt(liveRow.overhead)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmt(liveRow.packing)}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-black text-orange-600">{fmtCur(liveRow.grand_total)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">
                    {liveChange !== 0 && (
                      <span className={`font-bold ${liveChange > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {liveChange > 0 ? '+' : ''}{fmtCur(liveChange)}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2.5"></td>
                </tr>
                {history.length === 0 && (
                  <tr>
                    <td colSpan={15} className="py-8 text-center text-slate-400">No historical snapshots yet</td>
                  </tr>
                )}
                {withDelta.map((h, i) => {
                  const origIdx = getOriginalIdx(sorted[i]);
                  const rowBg = 'bg-white';
                  const label = getDisplayLabel(h, i);
                  return (
                  <tr key={`${origIdx}-${i}`}
                    className={`border-b border-slate-100 ${rowBg} hover:bg-slate-50`}
                    data-testid={`history-row-${i}`}>
                    <td className={`px-3 py-2.5 sticky left-0 z-10 ${rowBg} min-w-[160px] border-r border-slate-200`}>
                      <div className="flex flex-col gap-1">
                        {editingLabel === origIdx ? (
                          <div className="flex items-center gap-1">
                            <input
                              className="border border-slate-300 rounded px-1.5 py-0.5 text-xs w-[100px]"
                              value={labelInput}
                              onChange={(e) => setLabelInput(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && saveLabel(origIdx)}
                              autoFocus
                            />
                            <button onClick={() => saveLabel(origIdx)} className="text-emerald-600 hover:text-emerald-800 text-[10px] font-bold">OK</button>
                            <button onClick={() => setEditingLabel(null)} className="text-slate-400 hover:text-slate-600 text-[10px]">X</button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span
                              className="font-semibold cursor-pointer hover:text-blue-600 hover:underline text-slate-600"
                              title="Click to edit label"
                              onClick={() => { setEditingLabel(origIdx); setLabelInput(h.review_label || ''); }}
                            >
                              {label}
                            </span>
                          </div>
                        )}
                        <div className="relative">
                          <button
                            onClick={() => setDatePickerIdx(datePickerIdx === origIdx ? null : origIdx)}
                            className="text-[10px] text-blue-500 hover:text-blue-700 hover:underline flex items-center gap-0.5"
                            data-testid={`set-date-btn-${i}`}
                          >
                            <CalendarIcon size={9} />
                            {h.review_date || 'Set date'}
                          </button>
                          {datePickerIdx === origIdx && (
                            <div className="absolute top-5 left-0 z-50 bg-white border border-slate-200 rounded-md shadow-lg">
                              <DayPicker
                                mode="single"
                                selected={h.review_date ? (() => {
                                  const parts = h.review_date.split(/[-/.]/);
                                  if (parts.length === 3) {
                                    const [d, m, y] = parts.map(Number);
                                    return y > 1000 ? new Date(y, m - 1, d) : new Date(d, m - 1, y);
                                  }
                                  return undefined;
                                })() : undefined}
                                onSelect={(date) => date && saveDate(origIdx, date)}
                                className="p-2 text-xs"
                                classNames={{
                                  months: "flex flex-col",
                                  month: "space-y-2",
                                  caption: "flex justify-center pt-1 relative items-center",
                                  caption_label: "text-xs font-medium",
                                  nav: "space-x-1 flex items-center",
                                  nav_button: "h-6 w-6 bg-transparent p-0 opacity-50 hover:opacity-100 inline-flex items-center justify-center rounded-sm border border-slate-200",
                                  nav_button_previous: "absolute left-1",
                                  nav_button_next: "absolute right-1",
                                  table: "w-full border-collapse",
                                  head_row: "flex",
                                  head_cell: "text-slate-400 rounded-md w-7 font-normal text-[10px]",
                                  row: "flex w-full mt-1",
                                  cell: "relative p-0 text-center text-xs",
                                  day: "h-7 w-7 p-0 font-normal rounded-sm hover:bg-slate-100 inline-flex items-center justify-center cursor-pointer",
                                  day_selected: "bg-slate-900 text-white hover:bg-slate-800",
                                  day_today: "bg-orange-50 text-orange-700 font-bold",
                                }}
                                components={{
                                  IconLeft: () => <ChevronLeft className="h-3 w-3" />,
                                  IconRight: () => <ChevronRight className="h-3 w-3" />,
                                }}
                              />
                              <button onClick={() => setDatePickerIdx(null)}
                                className="w-full text-center text-[10px] text-slate-400 hover:text-slate-600 py-1 border-t">
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono whitespace-nowrap">
                      <span className="font-bold">{fmt(h.rm_price_per_kg)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-700">{fmt(h.gross_rmc)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-emerald-600">-{fmt(h.scrap_recovery)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{fmt(h.net_rmc)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmt(h.process_cost)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-purple-600">{fmt(h.plating_cost)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-indigo-600">{fmt(h.total_bop_cost)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-600">{fmt(h.tool_maintenance)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-slate-800">{fmt(h.total_cost)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmt(h.overhead)}</td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmt(h.packing)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-black text-orange-600">{fmtCur(h.grand_total)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {h.totalChange !== 0 && (
                        <span className={`font-bold ${h.totalChange > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                          {h.totalChange > 0 ? '+' : ''}{fmtCur(h.totalChange)}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-2.5 text-center">
                      <button
                        onClick={() => deleteEntry(origIdx)}
                        title="Delete this entry"
                        data-testid={`delete-history-${i}`}
                        className="p-1 text-slate-300 hover:text-red-500 transition-colors rounded-sm hover:bg-red-50"
                      >
                        <Trash2 size={11} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
